import { useEffect, useState } from "react";
import { useDebounce } from "@uidotdev/usehooks";
import { useSearchParams } from "react-router-dom";

import { SectionHeader } from "~/common/components";
import { consultationStatusOptions } from "~/common/constants/consultation";
import { NewConsultationModal } from "~/providers/components/consultationRequests/NewConsultationModal";
import { PATIENT_FILTER_PARAMS } from "~/providers/constants";
import {
  defaultConsultationParams,
  PatientListProviderFilters,
} from "~/providers/utils";
import { FilterResultSummary } from "../FilterResultSummary";
import SearchBar from "../SearchBar";
import { FilterOwnConsultationsButton } from "./FilterOwnConsultationsButton";

interface ConsultationRequestFiltersProps {
  isLoading: boolean;
  hasNoConsultations: boolean;
  isMultiProvider: boolean;
  filteredPatientsCount?: number;
  totalPatients?: number;
}

export const ConsultationRequestFilters = ({
  isLoading,
  hasNoConsultations,
  filteredPatientsCount,
  totalPatients,
  isMultiProvider,
}: ConsultationRequestFiltersProps) => {
  const [params, setParams] = useSearchParams(defaultConsultationParams);
  const paramName = params.get(PATIENT_FILTER_PARAMS.name) ?? undefined;
  const [patientFilter, setPatientFilter] = useState(paramName);

  const debouncedName = useDebounce(patientFilter, 600);

  useEffect(() => {
    const newParams = new URLSearchParams(params);
    if (debouncedName) {
      newParams.delete(PATIENT_FILTER_PARAMS.page);
      newParams.set(PATIENT_FILTER_PARAMS.name, debouncedName);
    } else {
      newParams.delete(PATIENT_FILTER_PARAMS.name);
    }
    setParams(newParams);
  }, [debouncedName, params, setParams]);

  const selectedFilters = params.getAll(
    PATIENT_FILTER_PARAMS.consultationStatus,
  );

  const hasAllFiltersApplied =
    selectedFilters.length === consultationStatusOptions.length;

  const disableSearchBarFilters =
    hasNoConsultations && hasAllFiltersApplied && !patientFilter;

  const onFilterChange = (filter: PatientListProviderFilters) => {
    params.set(PATIENT_FILTER_PARAMS.provider, filter);
    setParams(params);
  };

  return (
    <div className="flex flex-col items-start justify-between gap-4 md:flex-row">
      <SectionHeader className="flex flex-col text-xl font-semibold lg:text-3xl">
        Consultations
        <FilterResultSummary
          filteredPatientsCount={filteredPatientsCount}
          totalPatients={totalPatients}
        />
      </SectionHeader>
      <div className="flex flex-col items-start gap-4 md:flex-row">
        <div>
          <SearchBar
            className="h-11 w-full"
            disabled={disableSearchBarFilters}
            placeholder="Search by patient name"
            isLoading={isLoading}
            value={patientFilter}
            setValue={setPatientFilter}
          />
        </div>

        {isMultiProvider && (
          <div className="flex w-full shrink-0 items-center md:w-fit md:justify-end">
            <FilterOwnConsultationsButton
              filter={
                (params.get(
                  PATIENT_FILTER_PARAMS.provider,
                ) as PatientListProviderFilters) ??
                defaultConsultationParams.provider
              }
              onFilterChanged={onFilterChange}
            />
          </div>
        )}

        <div>
          <NewConsultationModal />
        </div>
      </div>
    </div>
  );
};
